/**
 * Monthly USD amounts — aligned with Stripe Price objects (see Stripe Dashboard / catalog).
 * Prepay: each line uses round(monthly × 36 × 0.9), same as separate Stripe prepay prices.
 */

export const STRIPE_PLAN_USD_MONTHLY = {
  small: { base: 250, maintenance: 75, support: 25 },
  medium: { base: 500, maintenance: 100, support: 50 },
  "large-medium": { base: 700, maintenance: 125, support: 75 },
  large: { base: 900, maintenance: 150, support: 100 },
} as const;

export type BillablePlanKey = keyof typeof STRIPE_PLAN_USD_MONTHLY;

export function formatUsdWhole(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

export const threeYearPrepayFromMonthly = (monthlyUsd: number) =>
  Math.round(monthlyUsd * 36 * 0.9);

/** Month-to-month total (matches sum of selected Stripe monthly prices). */
export function monthlyTotalFromSelections(args: {
  planKey: string;
  includeMaintenance: boolean;
  includeSupport: boolean;
}): number {
  const row =
    STRIPE_PLAN_USD_MONTHLY[args.planKey as BillablePlanKey] ?? undefined;
  if (!row) return 0;
  let t = row.base;
  if (args.includeMaintenance) t += row.maintenance;
  if (args.includeSupport) t += row.support;
  return t;
}

/**
 * 3-year prepay total — sum of per-line prepay rounds (matches Checkout with one price per line).
 */
export function prepayTotalFromSelections(args: {
  planKey: string;
  includeMaintenance: boolean;
  includeSupport: boolean;
}): number {
  const row =
    STRIPE_PLAN_USD_MONTHLY[args.planKey as BillablePlanKey] ?? undefined;
  if (!row) return 0;
  let total = threeYearPrepayFromMonthly(row.base);
  if (args.includeMaintenance)
    total += threeYearPrepayFromMonthly(row.maintenance);
  if (args.includeSupport) total += threeYearPrepayFromMonthly(row.support);
  return total;
}

export const planPrices: Record<string, number> = {
  small: STRIPE_PLAN_USD_MONTHLY.small.base,
  medium: STRIPE_PLAN_USD_MONTHLY.medium.base,
  "large-medium": STRIPE_PLAN_USD_MONTHLY["large-medium"].base,
  large: STRIPE_PLAN_USD_MONTHLY.large.base,
};

export const maintenancePrices: Record<string, number> = {
  small: STRIPE_PLAN_USD_MONTHLY.small.maintenance,
  medium: STRIPE_PLAN_USD_MONTHLY.medium.maintenance,
  "large-medium": STRIPE_PLAN_USD_MONTHLY["large-medium"].maintenance,
  large: STRIPE_PLAN_USD_MONTHLY.large.maintenance,
};

export const supportPrices: Record<string, number> = {
  small: STRIPE_PLAN_USD_MONTHLY.small.support,
  medium: STRIPE_PLAN_USD_MONTHLY.medium.support,
  "large-medium": STRIPE_PLAN_USD_MONTHLY["large-medium"].support,
  large: STRIPE_PLAN_USD_MONTHLY.large.support,
};
